import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * CareLogix™ ORACLE — Global Intelligence Chyron™ (Phase 18)
 *
 * Broadcast-grade lower-third intelligence feed:
 *  - Rotates live signals from /api/oracle/chyron (Signal Fabric + Admin + Status)
 *  - Severity-colored items (info · ok · warn · high · extreme)
 *  - Infinite CSS-driven scroll, pause-on-hover
 *  - Click target_url to deep-link into AdminOne (when configured)
 *
 * Strictly additive and z-ordered BELOW the LOGIX-2 floating dock so it never
 * occludes the dock's FAB / panel.
 */
const REFRESH_MS = 30_000;
// Slower, accessibility-first speeds. Optimized for monocular vision and
// reading comfort — the chyron should always be readable, not flashy.
const SPEED_MAP = {
  slow:   "320s",  // ~5.3 min per full cycle — very readable
  normal: "200s",  // ~3.3 min per full cycle — comfortable
  fast:   "120s",  // ~2 min per full cycle — minimum acceptable
};

const FALLBACK_ITEMS = [
  { id: "fb-0", text: "🌎 ORACLE GLOBAL INTELLIGENCE LIVE • STOCKS • FX • CRYPTO • HEALTHCARE • STARTUPS • GRANTS • SPORTS • LOGIX-2 MONITORING • HUMAN-IN-THE-LOOP REQUIRED", severity: "info", color: "#c5a059", icon: "🌎" },
  { id: "fb-1", text: "ORACLE Global Intelligence Operating System™ Online", severity: "ok", color: "#7ac860", icon: "🟢" },
  { id: "fb-2", text: "LOGIX-2™ Engine Synchronizing", severity: "warn", color: "#f5c842", icon: "🟡" },
  { id: "fb-3", text: "Educational market intelligence only · Not financial or betting advice · No guaranteed outcomes · Human confirmation required", severity: "info", color: "#60b8e8", icon: "🔵" },
];

function chyronApi() {
  // Independent fetch to /api/oracle/chyron (no auth required)
  const base = process.env.REACT_APP_BACKEND_URL;
  return fetch(`${base}/api/oracle/chyron?limit=30`, { cache: "no-store" }).then((r) => r.json());
}

export default function GlobalIntelligenceChyron({
  variant = "public",       // "public" | "compact" | "admin"
  className = "",
  intervalMs = REFRESH_MS,
} = {}) {
  const [items, setItems] = useState(FALLBACK_ITEMS);
  // Default to SLOW for accessibility — readable for monocular / low-vision users.
  const [settings, setSettings] = useState({ enabled: true, speed: "slow", pause_on_hover: true });
  const [enabled, setEnabled] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  // Phase 7 — Hard-dismiss state. Persisted to localStorage. Once a user
  // dismisses the chyron, it stays gone for that browser until they restore
  // it (e.g. via Animation Settings).
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("carelogix_chyron_dismissed") === "1"; } catch { return false; }
  });
  // Phase 7 — On mobile we render statically (no fixed positioning) so the
  // chyron never sits on top of CTAs or content. Detected via a media query.
  const [isMobile, setIsMobile] = useState(() => {
    try { return typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", onChange); } catch { mq.addListener(onChange); }
    return () => { try { mq.removeEventListener("change", onChange); } catch { mq.removeListener(onChange); } };
  }, []);
  const [hover, setHover] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  // Initial + interval poll
  useEffect(() => {
    mountedRef.current = true;
    const fetchNow = async () => {
      try {
        const data = await chyronApi();
        if (!mountedRef.current) return;
        if (data?.enabled === false) {
          setEnabled(false);
          return;
        }
        if (Array.isArray(data?.items) && data.items.length > 0) {
          setItems(data.items);
        }
        if (data?.settings) {
          setSettings(data.settings);
          if (typeof data.settings.enabled === "boolean") setEnabled(data.settings.enabled);
        }
      } catch {
        // Keep last known items / fallback — never break the host UI.
      }
    };
    fetchNow();
    timerRef.current = setInterval(fetchNow, Math.max(8000, intervalMs));
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [intervalMs, refreshFlag]);

  // If user collapsed the chyron, persist it. Phase 7 — also auto-collapse
  // by default on the very first mobile visit so the strip never covers
  // hero CTAs out of the box.
  useEffect(() => {
    try {
      const c = localStorage.getItem("carelogix_chyron_collapsed");
      if (c === "1") { setCollapsed(true); return; }
      if (c === null && typeof window !== "undefined" && window.matchMedia &&
          window.matchMedia("(max-width: 640px)").matches) {
        // First visit on mobile → start collapsed; users can expand via "◀ Chyron".
        setCollapsed(true);
        try { localStorage.setItem("carelogix_chyron_collapsed", "1"); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem("carelogix_chyron_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Phase 7 — Hard dismiss. Persists to localStorage and unmounts the strip
  // for the rest of the session.
  const dismiss = useCallback(() => {
    try { localStorage.setItem("carelogix_chyron_dismissed", "1"); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  const onItemClick = useCallback((it) => {
    const url = it?.target_url;
    if (!url) return;
    // Internal AdminOne deep-link via hash router; external otherwise.
    if (url.startsWith("/")) {
      window.location.assign(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const speedKey = (settings?.speed || "slow").toLowerCase();
  const animDuration = SPEED_MAP[speedKey] || SPEED_MAP.slow;
  const pauseOnHover = settings?.pause_on_hover !== false;

  // Phase 7 — Severity + text filter for public visitors:
  //   • Public users only see neutral status items (info / ok)…
  //   • …AND items whose text does NOT mention admin/infra terms.
  // System warnings, high/extreme alerts, and any text referencing
  // AdminOne / Mongo / LLM / OANDA / provider keys are reserved for
  // AdminOne surfaces (where variant === "admin").
  const PUBLIC_SAFE_SEVERITIES = new Set(["info", "ok", undefined, null, ""]);
  const PUBLIC_FORBIDDEN_RX = /(adminone|mongo|llm\b|openai|anthropic|oanda|api[_\s-]?key|provider[_\s-]?key|secret|jwt|bearer|\/api\/adminone)/i;
  // For an infinite, seamless scroll we triple the items.
  const displayItems = useMemo(() => {
    let safe = (items || []).filter((it) => it && it.text);
    if (variant !== "admin") {
      safe = safe.filter((it) => PUBLIC_SAFE_SEVERITIES.has((it.severity || "").toLowerCase()));
      safe = safe.filter((it) => !PUBLIC_FORBIDDEN_RX.test(String(it.text || "")));
    }
    if (safe.length === 0) {
      // Public fallback is only the neutral status lines.
      const neutralFallback = FALLBACK_ITEMS.filter((it) =>
        PUBLIC_SAFE_SEVERITIES.has((it.severity || "").toLowerCase()) &&
        !PUBLIC_FORBIDDEN_RX.test(String(it.text || ""))
      );
      safe = variant === "admin" ? FALLBACK_ITEMS : neutralFallback;
    }
    return [...safe, ...safe, ...safe];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, variant]);

  if (!enabled) return null;
  // Phase 7 — User dismissed. Render nothing.
  if (dismissed) return null;

  return (
    <div
      className={`gic gic--${variant} ${collapsed ? "gic--collapsed" : ""} ${isMobile ? "gic--mobile-static" : ""} ${className}`}
      data-testid="global-intel-chyron"
      role="region"
      aria-label="Global Intelligence Chyron"
      style={isMobile ? { position: "static", bottom: "auto", left: "auto", right: "auto" } : undefined}
    >
      <button
        type="button"
        className="gic__toggle"
        onClick={toggleCollapsed}
        aria-label={collapsed ? "Show intelligence chyron" : "Hide intelligence chyron"}
        data-testid="global-intel-chyron-toggle"
        title={collapsed ? "Show intelligence chyron" : "Hide intelligence chyron"}
      >
        {collapsed ? "◀ Chyron" : "✕"}
      </button>
      {/* Phase 7 — Hard dismiss button. Persists to localStorage. */}
      <button
        type="button"
        className="gic__dismiss"
        onClick={dismiss}
        aria-label="Dismiss intelligence strip"
        data-testid="global-intel-chyron-dismiss"
        title="Dismiss"
        style={{ position: "absolute", right: 28, top: 4, background: "transparent", color: "rgba(245,245,220,0.55)", border: "none", fontSize: 11, cursor: "pointer", padding: "2px 6px" }}
      >
        Dismiss
      </button>

      {!collapsed && (
        <>
          <div className="gic__brand" aria-hidden="true">
            <span className="gic__brand-dot" />
            <span className="gic__brand-label">LIVE</span>
            <span className="gic__brand-sub">Global Intelligence Chyron™</span>
          </div>
          <div
            className="gic__viewport"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid="global-intel-chyron-viewport"
          >
            <div
              className="gic__track"
              style={{
                animationDuration: animDuration,
                animationPlayState: hover && pauseOnHover ? "paused" : "running",
              }}
              data-testid="global-intel-chyron-track"
            >
              {displayItems.map((it, i) => {
                const tone = (it.severity || "ok").toLowerCase();
                const color = it.color || "#7ac860";
                const clickable = !!it.target_url;
                return (
                  <button
                    type="button"
                    key={`${it.id || it.text}-${i}`}
                    className={`gic__item gic__item--${tone} ${clickable ? "is-clickable" : ""}`}
                    style={{ color }}
                    onClick={() => clickable && onItemClick(it)}
                    title={clickable ? `Open ${it.target_url}` : undefined}
                    data-testid={`global-intel-chyron-item-${i}`}
                    tabIndex={clickable ? 0 : -1}
                  >
                    <span className="gic__icon" aria-hidden="true">{it.icon || "◆"}</span>
                    <span className="gic__sev" style={{ borderColor: color }}>{(it.severity || "OK").toUpperCase()}</span>
                    <span className="gic__text">{it.text}</span>
                    {typeof it.oracle_score === "number" && (
                      <span className="gic__score">OS&nbsp;{it.oracle_score}</span>
                    )}
                    {it.watchtower && <span className="gic__src">· {String(it.watchtower).toUpperCase()}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
